import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactResizeDetector from 'react-resize-detector';

import { contentfulClient } from 'src/config/contentfulClient';
import closeIcon from 'src/public/img/close-icon-white.svg';
import { delay } from 'src/shared/helpers/delay';
import { color, font } from 'src/shared/styles/variables';

const Wrapper = styled.div({
    maxWidth: 1440,
    display: 'flex',
    position: 'relative',
    justifyContent: 'center',
    width: '100%',
    margin: '0 auto',
    backgroundColor: color.ebonyClay,
    transition: 'all .3s ease-in-out',
});

const TextWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    padding: '14px 0',
});

const MainText = styled.p({
    textAlign: 'center',
    color: color.white,
    ...font.book(font.size.base),
});

const LearnMoreText = styled.span({
    color: color.white,
    marginLeft: 5,
    textDecoration: 'underline',
    cursor: 'pointer',
    ...font.heavy(font.size.base),
});

const CloseButtonWrapper = styled.div({
    position: 'absolute',
    top: 0,
    right: 0,
    padding: '14px 19px',
    cursor: 'pointer',
    'svg path': {
        stroke: color.white,
    },
});

interface ContentfulResponse {
    items: {
        fields: {
            link?: string;
            text?: string;
            uuid?: string;
        };
        sys: unknown;
    }[];
}

export const STORAGE_KEY = 'dismissed_banner';

export const TimelyResponseBanner = () => {
    const [link, setLink] = useState('');
    const [text, setText] = useState('');
    const [uuid, setUuid] = useState('');

    const { t, i18n } = useTranslation();

    useEffect(() => {
        const fetchBannerData = async () => {
            await delay(200);

            try {
                const data: ContentfulResponse = await contentfulClient.getEntries({
                    content_type: 'timelyResponseBanner',
                });

                const [banner] = data.items?.slice(-1);

                if (!banner) {
                    return;
                }

                const { fields } = banner;

                if (!fields.text || !fields.link || !fields.uuid) {
                    return;
                }

                const dismissedBannerUuid = localStorage.getItem(STORAGE_KEY);

                if (fields.uuid === dismissedBannerUuid) {
                    return;
                }

                // ReactGA.event({
                //     category: 'Timely response banner',
                //     action: 'Displayed timely response banner',
                //     label: fields.text,
                // });

                if (isCanceled) {
                    return;
                }

                setUuid(fields.uuid);
                setLink(fields.link);
                setText(fields.text);
            } catch (err) {
                Sentry.captureException(err);
            }
        };

        let isCanceled = false;
        if (i18n.language === 'en-US') {
            fetchBannerData();
        }

        return () => {
            isCanceled = true;
        };
    }, [i18n.language]);

    const handleBannerClick = () => {
        // TODO: Firebase Analytics Removal
        // ReactGA.event({
        //     category: 'Timely response banner',
        //     action: 'Clicked timely response banner',
        //     label: text,
        // });

        window.open(link, '_blank');
    };

    const hideBanner = async () => {
        // TODO: Firebase Analytics Removal
        // ReactGA.event({
        //     category: 'Timely response banner',
        //     action: 'Closed timely response banner',
        //     label: text,
        // });

        localStorage.setItem(STORAGE_KEY, uuid);

        setUuid('');
    };

    return (
        <ReactResizeDetector handleHeight>
            {({ height = 49 }) => (
                <Wrapper
                    style={{
                        marginTop: uuid ? 0 : -height,
                        opacity: uuid ? 1 : 0,
                    }}
                    data-testid="timelyBanner"
                >
                    <TextWrapper>
                        <MainText>{text}</MainText>
                        <LearnMoreText onClick={handleBannerClick}>
                            {t('dashboard:learn_more')}
                        </LearnMoreText>
                    </TextWrapper>

                    <CloseButtonWrapper onClick={hideBanner}>
                        <img src={closeIcon} alt={t('common.close')} />
                    </CloseButtonWrapper>
                </Wrapper>
            )}
        </ReactResizeDetector>
    );
};
